/**
 * GeneralInfoSection component for the dish form
 */

import React, { useState, useEffect } from 'react';
import InputField from '../comman/input.js';
import CustomSelect from '../comman/select.js';
import CustomMultiSelect from '../comman/multi-select.js';
import Toggle from '../comman/toggle.js';
import { getTranslation } from '../../utils/translate.js';
import { DISH_TYPES, spiceApiKeyToTranslationKey } from '../../utils/dishFormUtils.js';
import { 
  fetchDishTypes,
  fetchDishDietaryOptions, 
  fetchAdjustableSpiceLevels 
} from '../../services/menu-service.js';

// Fallback dish types based on translations
const DISH_TYPE_OPTIONS = {
  BREAKFAST: 'BREAKFAST',
  LUNCH: 'LUNCH',
  DINNER: 'DINNER',
  SIDE: 'SIDE',
  APPETIZER: 'APPETIZER',
  DESSERT: 'DESSERT',
  SEMIFINISHED: 'SEMIFINISHED'
};

// Module-level cache to prevent duplicate API calls across component remounts
const dataCache = {
  dishTypes: null,
  dietaryOptions: null,
  spiceLevels: null,
  isFetchingDishTypes: false,
  isFetchingDietaryOptions: false,
  isFetchingSpiceLevels: false
};

/**
 * GeneralInfoSection component
 * @param {Object} props - Component props
 * @returns {JSX.Element} GeneralInfoSection component
 */
const GeneralInfoSection = ({
  formData,
  errors,
  dishTypeOptions: propsDishTypeOptions,
  spiceLevelOptions: propsSpiceLevelOptions,
  dietaryOptionOptions: propsDietaryOptionOptions,
  handleInputChange,
  handleSingleSelectChange,
  handleMultiSelectChange,
  handleTranslateAll,
  isTranslating,
  language,
  setErrors
}) => {
  // State for fetched data - initialize from cache if available to prevent race condition
  const [fetchedDishTypes, setFetchedDishTypes] = useState(() => {
    // Initialize from cache if available (synchronous)
    if (!propsDishTypeOptions || propsDishTypeOptions.length === 0) {
      return dataCache.dishTypes || [];
    }
    return [];
  });
  const [isPreorder, setIsPreorder] = useState(formData?.orderBefore > 0);
  const [fetchedDietaryOptions, setFetchedDietaryOptions] = useState(() => {
    // Initialize from cache if available (synchronous)
    if (!propsDietaryOptionOptions || propsDietaryOptionOptions.length === 0) {
      return dataCache.dietaryOptions || [];
    }
    return [];
  });
  const [fetchedSpiceLevels, setFetchedSpiceLevels] = useState(() => {
    // Initialize from cache if available (synchronous)
    if (!propsSpiceLevelOptions || propsSpiceLevelOptions.length === 0) {
      return dataCache.spiceLevels || [];
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState({
    dishTypes: false,
    dietaryOptions: false,
    spiceLevels: false
  });

  // Fetch data on component mount (only if not already in cache/state)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch dish types if not provided from props and not already loaded
        if ((!propsDishTypeOptions || propsDishTypeOptions.length === 0) && 
            (!fetchedDishTypes || fetchedDishTypes.length === 0)) {
          // Skip if already fetching or already cached (state initialized from cache)
          if (dataCache.isFetchingDishTypes || dataCache.dishTypes) return;
          
          dataCache.isFetchingDishTypes = true;
          setIsLoading(prev => ({ ...prev, dishTypes: true }));
          try {
            const dishTypesData = await fetchDishTypes();
            if (dishTypesData) {
              dataCache.dishTypes = dishTypesData;
              setFetchedDishTypes(dishTypesData);
            }
          } finally {
            setIsLoading(prev => ({ ...prev, dishTypes: false }));
            dataCache.isFetchingDishTypes = false;
          }
        }

        // Fetch dietary options if not provided from props and not already loaded
        if ((!propsDietaryOptionOptions || propsDietaryOptionOptions.length === 0) && 
            (!fetchedDietaryOptions || fetchedDietaryOptions.length === 0)) {
          // Skip if already fetching or already cached (state initialized from cache)
          if (dataCache.isFetchingDietaryOptions || dataCache.dietaryOptions) return;
          
          dataCache.isFetchingDietaryOptions = true;
          setIsLoading(prev => ({ ...prev, dietaryOptions: true }));
          try {
            const dietaryOptionsData = await fetchDishDietaryOptions();
            if (dietaryOptionsData) {
              dataCache.dietaryOptions = dietaryOptionsData;
              setFetchedDietaryOptions(dietaryOptionsData);
            }
          } finally {
            setIsLoading(prev => ({ ...prev, dietaryOptions: false }));
            dataCache.isFetchingDietaryOptions = false;
          }
        }

        // Fetch spice levels if not provided from props and not already loaded
        if ((!propsSpiceLevelOptions || propsSpiceLevelOptions.length === 0) && 
            (!fetchedSpiceLevels || fetchedSpiceLevels.length === 0)) {
          // Skip if already fetching or already cached (state initialized from cache)
          if (dataCache.isFetchingSpiceLevels || dataCache.spiceLevels) return;
          
          dataCache.isFetchingSpiceLevels = true;
          setIsLoading(prev => ({ ...prev, spiceLevels: true }));
          try {
            const spiceLevelsData = await fetchAdjustableSpiceLevels();
            if (spiceLevelsData) {
              dataCache.spiceLevels = spiceLevelsData;
              setFetchedSpiceLevels(spiceLevelsData);
            }
          } finally {
            setIsLoading(prev => ({ ...prev, spiceLevels: false }));
            dataCache.isFetchingSpiceLevels = false;
          }
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        setIsLoading({
          dishTypes: false,
          dietaryOptions: false,
          spiceLevels: false
        });
        // Reset cache flags on error
        dataCache.isFetchingDishTypes = false;
        dataCache.isFetchingDietaryOptions = false;
        dataCache.isFetchingSpiceLevels = false;
      }
    };

    fetchData();
  }, [propsDishTypeOptions, propsDietaryOptionOptions, propsSpiceLevelOptions, fetchedDishTypes, fetchedDietaryOptions, fetchedSpiceLevels]);

  // Create dish type options from DISH_TYPES if not provided from props
  const dishTypeSelectOptions = React.useMemo(() => {
    // If dishTypeOptions from props is not empty, use those
    if (propsDishTypeOptions && propsDishTypeOptions.length > 0) {
      return propsDishTypeOptions;
    }

    // If we have fetched dish types, use those
    if (fetchedDishTypes && fetchedDishTypes.length > 0) {
      return fetchedDishTypes.map(type => ({
        id: type,
        value: type,
        label: getTranslation(`dishTypes.${type}`, language) || type
      }));
    }

    // Otherwise use fallback options
    return Object.entries(DISH_TYPE_OPTIONS).map(([key, value]) => ({
      id: value,
      value: value,
      label: getTranslation(`dishTypes.${value}`, language) || value
    }));
  }, [propsDishTypeOptions, fetchedDishTypes, language]);

  // Use provided spice level options or fetched ones
  const spiceLevelSelectOptions = React.useMemo(() => {
    if (propsSpiceLevelOptions && propsSpiceLevelOptions.length > 0) {
      return propsSpiceLevelOptions;
    }

    if (fetchedSpiceLevels && fetchedSpiceLevels.length > 0) {
      return fetchedSpiceLevels.map(level => {
        const apiKey = level.spiceLevelApiKey;
        const translationKey = apiKey ? spiceApiKeyToTranslationKey(apiKey) : "";
        const label = translationKey
          ? (getTranslation(`spiceLevels.${translationKey}`, language) || level.spiceLevel)
          : level.spiceLevel;
        return {
          id: level.id,
          value: level.id,
          spiceLevelApiKey: apiKey,
          label
        };
      });
    }

    return [];
  }, [propsSpiceLevelOptions, fetchedSpiceLevels, language]);

  // Use provided dietary options or fetched ones
  const dietaryOptionSelectOptions = React.useMemo(() => {
    if (propsDietaryOptionOptions && propsDietaryOptionOptions.length > 0) {
      return propsDietaryOptionOptions;
    }

    if (fetchedDietaryOptions && fetchedDietaryOptions.length > 0) {
      return fetchedDietaryOptions.map(option => ({
        id: option.id,
        value: option.dietaryOptionValue,
        dietaryOptionValue: option.dietaryOptionValue,
        label: getTranslation(`dietaryOptions.${option.dietaryOptionValue}`, language) || option.dietaryOptionValue
      }));
    }

    return [];
  }, [propsDietaryOptionOptions, fetchedDietaryOptions, language]);

  // Find the selected spice level option
  // Backend returns spiceLevel as Integer (1-4), ensure type matching
  const selectedSpiceLevelOption = React.useMemo(() => {
    if (!formData.spiceLevel) return null;
    // Convert both to numbers for comparison to handle string/number mismatch
    const spiceLevelValue = Number(formData.spiceLevel);
    return spiceLevelSelectOptions.find(option => Number(option.value) === spiceLevelValue);
  }, [formData.spiceLevel, spiceLevelSelectOptions]);

  // Check if selected spice level is "not spicy"
  const isNotSpicy = React.useMemo(() => {
    if (!selectedSpiceLevelOption) return false;
    const label = selectedSpiceLevelOption.label?.toLowerCase() || '';
    return label.includes('not') || label.includes('ոչ') || 
           selectedSpiceLevelOption.label === 'Ոչ կծու' ||
           selectedSpiceLevelOption.label === 'Not spicy' ||
           selectedSpiceLevelOption.label === 'Не острый';
  }, [selectedSpiceLevelOption]);

  const handleToggleChange = () => {
    setIsPreorder((prev) => {
      const newValue = !prev;
      if (!newValue) {
        handleInputChange({
          target: {
            name: 'orderBefore',
            value: '0'
          }
        });
      }
      return newValue;
    });
  };

  const handleSpiceLevelChange = (selectedOption) => {
    handleSingleSelectChange(selectedOption, "spiceLevel");

    if (selectedOption) {
      // Find the spice level option to check if it's "not spicy"
      const selectedSpiceLevelOption = spiceLevelSelectOptions.find(opt => Number(opt.value) === Number(selectedOption.value));
      const isNotSpicy = selectedSpiceLevelOption?.label?.toLowerCase().includes('not') || 
                         selectedSpiceLevelOption?.label?.toLowerCase().includes('ոչ') ||
                         selectedSpiceLevelOption?.label === 'Ոչ կծու' ||
                         selectedSpiceLevelOption?.label === 'Not spicy' ||
                         selectedSpiceLevelOption?.label === 'Не острый';

      if (isNotSpicy) {
        // If "not spicy" is selected, clear and disable adjustable spice levels
        handleMultiSelectChange([], "adjustableSpiceLevelDtoList");
      } else {
        // If a spice level is selected (and it's not "not spicy"), make sure it's in the adjustable spice levels list
        const adjustableSpiceLevels = [...(formData.adjustableSpiceLevelDtoList || [])];

        // Check if the selected spice level is already in the list (ensure type matching)
        const isAlreadyIncluded = adjustableSpiceLevels.some(level => Number(level.id) === Number(selectedOption.value));

        // If not already included, add it to the list
        if (!isAlreadyIncluded) {
          // Find the full option from the options list (ensure type matching)
          const optionToAdd = spiceLevelSelectOptions.find(opt => Number(opt.value) === Number(selectedOption.value));
          if (optionToAdd) {
            // Add the selected spice level to adjustable levels
            handleMultiSelectChange(
              [...adjustableSpiceLevels.map(level => 
                spiceLevelSelectOptions.find(opt => Number(opt.value) === Number(level.id))
              ).filter(Boolean), optionToAdd], 
              "adjustableSpiceLevelDtoList"
            );
          }
        }
      }
    } else {
      // If spice level is cleared, also clear adjustable spice levels
      handleMultiSelectChange([], "adjustableSpiceLevelDtoList");
    }
  };

  // Custom handler for adjustable spice levels changes
  const handleAdjustableSpiceLevelsChange = (selectedOptions) => {
    // If there's a selected spice level, ensure it's always included
    if (selectedSpiceLevelOption) {
      // Check if the selected spice level is in the newly selected options (ensure type matching)
      const hasSelectedSpiceLevel = selectedOptions.some(opt => Number(opt.value) === Number(selectedSpiceLevelOption.value));

      if (!hasSelectedSpiceLevel) {
        // If not, add it back to the selection
        selectedOptions = [...selectedOptions, selectedSpiceLevelOption];
      }
    }

    // Call the original handler with the possibly modified selection
    handleMultiSelectChange(selectedOptions, "adjustableSpiceLevelDtoList");
  };

  return (
    <div className="general-info-section">
      {/* Section header with improved visual design */}
      <h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-[#c9d1d9] border-b border-gray-200 dark:border-[#30363d] pb-3">
        {getTranslation('menu.generalInfo', language)}
      </h2>

      {/* Main form grid with responsive layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-6 gap-y-5">
        {/* Left column - primary information */}
        <div className="space-y-5">
          {/* Name fields - Armenian, Russian, English */}
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.nameHy} 
              name="nameHy"
              label={getTranslation('menu.nameArmenian', language)} 
              labelFor="name-hy"
              placeholder={getTranslation('menu.namePlaceholder', language)} 
              isRequired={true}
              error={errors?.nameHy}
              className="w-full"
            />
          </div>
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.nameRu || ''} 
              name="nameRu"
              label={getTranslation('menu.nameRussian', language) || 'Name (Russian)'} 
              labelFor="name-ru"
              placeholder={getTranslation('menu.namePlaceholder', language)} 
              isRequired={false}
              error={errors?.nameRu}
              className="w-full"
            />
          </div>
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.nameEn || ''} 
              name="nameEn"
              label={getTranslation('menu.nameEnglish', language) || 'Name (English)'} 
              labelFor="name-en"
              placeholder={getTranslation('menu.namePlaceholder', language)} 
              isRequired={false}
              error={errors?.nameEn}
              className="w-full"
            />
          </div>

          {/* Translate All Button */}
          {handleTranslateAll && (
            <div className="form-field flex justify-center mt-2">
              <button
                type="button"
                onClick={handleTranslateAll}
                disabled={isTranslating}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                {isTranslating ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {getTranslation('menu.translating', language) || 'Translating...'}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    {getTranslation('menu.translateAll', language) || 'Translate All'}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Price field */}
          <div className="form-field">
            <InputField
              onChange={handleInputChange}
              value={formData.price}
              name="price"
              inputType="text"
              label={getTranslation('menu.priceAMD', language)}
              labelFor="price-amd"
              placeholder="0"
              isRequired={true}
              error={errors?.price}
              min="0"
              className="w-full"
            />
          </div>

          {/* Dish type field */}
          <div className="form-field">
            <CustomSelect
              fieldName="type"
              name="type"
              onChange={(selectedOption) => handleSingleSelectChange(selectedOption, "type")}
              value={formData.type}
              label={getTranslation('menu.dishType', language)}
              options={dishTypeSelectOptions}
              placeholder={getTranslation('menu.selectType', language)}
              isRequired={true}
              error={errors?.type}
              setError={(f, m) => setErrors(p => ({...p, [f]: m}))}
              className="w-full"
              isLoading={isLoading.dishTypes}
            />
          </div>

          {/* Pre-order days field */}
          <div className="form-field">
              <Toggle
                  id="toggle"
                  checked={!isPreorder}
                  onChange={handleToggleChange}
                  className="flex items-center justify-between w-full sm:justify-start"
                  toggleClassName={isPreorder ? 'bg-gray-400 dark:bg-[#30363d]' : 'bg-gray-900 dark:bg-[#484f58]'}
                  renderLabel={(toggleId) => (
                      <label htmlFor={toggleId} className={`text-sm font-medium flex items-center mr-4 text-gray-900 dark:text-[#c9d1d9]`}>
                        {isPreorder
                            ? <span className="inline-flex items-center">
                                                      <span className="w-2 h-2 bg-gray-400 dark:bg-[#6e7681] rounded-full mr-4 animate-pulse"></span>
                              {getTranslation('menu.preOrder', language)}
                                                  </span>
                            : <span className="inline-flex items-center">
                                                      <span className="w-2 h-2 bg-gray-900 dark:bg-[#c9d1d9] rounded-full mr-4"></span>
                              {getTranslation('menu.sameDayOrder', language)}
                                                  </span>
                        }
                      </label>
                  )}
              />

            {isPreorder && (
              <InputField 
                onChange={handleInputChange} 
                value={formData.orderBefore} 
                name="orderBefore"
                inputType="number" 
                label={getTranslation('menu.preOrderDays', language)}
                labelFor="order-before"
                placeholder={getTranslation('menu.preOrderDaysPlaceholder', language)}
                isRequired={false}
                error={errors?.orderBefore}
                className="w-full"
              />
            )}
          </div>
        </div>

        {/* Right column - secondary information */}
        <div className="space-y-5">
          {/* Expiration days field */}
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.expirationDate} 
              name="expirationDate"
              inputType="number" 
              label={getTranslation('menu.expirationDays', language)}
              labelFor="expiration-days"
              placeholder={getTranslation('menu.expirationDaysPlaceholder', language)}
              isRequired={true} 
              error={errors?.expirationDate}
              className="w-full"
            />
          </div>

          {/* Minimum Order Count field */}
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.minimumOrderCount}
              name="minimumOrderCount"
              inputType="number"
              label={getTranslation('menu.minimumOrderCount', language) || "Minimum Order Count"}
              labelFor="minimum-order-count"
              placeholder="1"
              isRequired={false} 
              error={errors?.minimumOrderCount}
              className="w-full"
            />
          </div>

          {/* Adults Only toggle */}
          <div className="form-field">
            <Toggle
              id="adults-only-toggle"
              checked={formData.adultsOnly === 1}
              onChange={(e) => {
                handleInputChange({
                  target: {
                    name: 'adultsOnly',
                    value: e.target.checked ? 1 : 0
                  }
                });
              }}
              className="flex items-center justify-between w-full sm:justify-start"
              toggleClassName={formData.adultsOnly === 1 ? 'bg-gray-900 dark:bg-[#484f58]' : 'bg-gray-400 dark:bg-[#30363d]'}
              renderLabel={(toggleId) => (
                <label htmlFor={toggleId} className={`text-sm font-medium flex items-center mr-4 text-gray-900 dark:text-[#c9d1d9]`}>
                  {getTranslation('menu.adultsOnly', language) || 'Adults Only (18+)'}
                </label>
              )}
            />
          </div>

          {/* Portion fields - Armenian, Russian, English */}
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.portionHy || ''} 
              name="portionHy"
              label={getTranslation('menu.portionArmenian', language)} 
              labelFor="portion-hy"
              placeholder={getTranslation('menu.portionPlaceholder', language)} 
              isRequired={false}
              error={errors?.portionHy}
              className="w-full"
            />
          </div>
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.portionRu || ''} 
              name="portionRu"
              label={getTranslation('menu.portionRussian', language) || 'Portion (Russian)'} 
              labelFor="portion-ru"
              placeholder={getTranslation('menu.portionPlaceholder', language)} 
              isRequired={false}
              error={errors?.portionRu}
              className="w-full"
            />
          </div>
          <div className="form-field">
            <InputField 
              onChange={handleInputChange} 
              value={formData.portionEn || ''} 
              name="portionEn"
              label={getTranslation('menu.portionEnglish', language) || 'Portion (English)'} 
              labelFor="portion-en"
              placeholder={getTranslation('menu.portionPlaceholder', language)} 
              isRequired={false}
              error={errors?.portionEn}
              className="w-full"
            />
          </div>

          {/* Dietary options field */}
          <div className="form-field">
            <CustomMultiSelect
              onChange={(selectedOptions) => handleMultiSelectChange(selectedOptions, "dietaryOptionDtoList")}
              value={formData.dietaryOptionDtoList?.map(dto => dietaryOptionSelectOptions.find(opt => opt.id === dto.id)).filter(Boolean) || []}
              name="dietaryOptionDtoList"
              fieldName="dietaryOptionDtoList"
              label={getTranslation('menu.dietaryOptions', language)}
              options={dietaryOptionSelectOptions}
              placeholder={getTranslation('menu.selectDietaryOptions', language)}
              className="w-full"
              isLoading={isLoading.dietaryOptions}
            />
          </div>
        </div>
      </div>

      {/* Spice level section - conditionally shown and full width */}
      {formData.type && formData.type !== DISH_TYPES.DESSERT && (
        <div className="mt-6 pt-5 border-t border-gray-100 dark:border-[#30363d]">
          <h3 className="text-base font-medium mb-4 text-gray-700 dark:text-[#b1bac4]">
            {getTranslation('menu.spiceSettings', language)}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-field">
              <CustomSelect
                fieldName="spiceLevel"
                name="spiceLevel"
                onChange={handleSpiceLevelChange}
                value={formData.spiceLevel ? Number(formData.spiceLevel) : null}
                label={getTranslation('menu.spiceLevel', language)}
                options={spiceLevelSelectOptions.map(option => ({
                  ...option,
                  value: Number(option.value)
                }))}
                placeholder={getTranslation('menu.selectSpiceLevel', language)}
                isRequired={true}
                error={errors?.spiceLevel}
                setError={(f, m) => setErrors(p => ({...p, [f]: m}))}
                className="w-full"
                isLoading={isLoading.spiceLevels}
              />
            </div>
            <div className="form-field">
              <CustomMultiSelect
                onChange={handleAdjustableSpiceLevelsChange}
                value={isNotSpicy ? [] : (formData.adjustableSpiceLevelDtoList?.map(item => {
                  // Ensure type matching for comparison
                  const found = spiceLevelSelectOptions.find(opt => Number(opt.value) === Number(item.id));
                  return found ? { ...found, value: Number(found.value) } : null;
                }).filter(Boolean) || [])}
                name="adjustableSpiceLevelDtoList"
                fieldName="adjustableSpiceLevelDtoList"
                label={getTranslation('menu.possibleSpiceLevels', language)}
                options={spiceLevelSelectOptions.map(opt => ({
                  ...opt,
                  value: Number(opt.value) // Ensure value is number
                }))}
                placeholder={isNotSpicy 
                  ? (getTranslation('menu.notSpicyNoPossibleLevels', language) || "Not applicable for non-spicy dishes")
                  : getTranslation('menu.selectPossibleSpiceLevels', language)
                }
                className="w-full"
                isLoading={isLoading.spiceLevels}
                isDisabled={!formData.spiceLevel || isNotSpicy}
                getOptionDisabled={(option) => formData.spiceLevel && Number(option.value) === Number(formData.spiceLevel)}
                noOptionsMessage={() => {
                  if (isNotSpicy) {
                    return getTranslation('menu.notSpicyNoPossibleLevels', language) || "Not applicable for non-spicy dishes";
                  }
                  return !formData.spiceLevel 
                    ? getTranslation('menu.selectSpiceLevelFirst', language) || "Please select a spice level first" 
                    : getTranslation('menu.noOptions', language) || "No options available";
                }}
              />
              {formData.spiceLevel && !isNotSpicy && (
                <p className="text-xs text-gray-500 dark:text-[#8b949e] mt-1 font-medium">
                  {"The primary spice level is always included"}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralInfoSection;
